/**
 * WMS popup header — one compact header for every WMS modal, drawer and profile.
 *
 * The colour is the shared module masthead (see WMS_POPUP_GRADIENT below). The
 * shape is the compact Work Profile header: ~44px tall, 14px title, 28px icon box and
 * 28px close button. Before this, the module's ~150 popups carried about 30 different
 * header styles (purple, indigo, slate, amber…, py-4 bars with 40–48px icons).
 *
 * Action popups (approve / reject / hindrance / warning) keep their signal colour via
 * wmsPopupHeader('red' | 'green' | 'amber') — same shape, different gradient. Small
 * "Are you sure?" confirms stay plain white and do not use this.
 *
 * Usage:
 *   <div className={WMS_POPUP_HEADER}>
 *     <div className="flex items-center gap-2 min-w-0">
 *       <div className={WMS_POPUP_ICON}><i className="ri-file-list-3-line" /></div>
 *       <div className="min-w-0">
 *         <h2 className={WMS_POPUP_TITLE}>Title</h2>
 *         <p className={WMS_POPUP_SUBTITLE}>Subtitle</p>
 *       </div>
 *     </div>
 *     <button className={WMS_POPUP_CLOSE} onClick={onClose}><i className="ri-close-line text-lg" /></button>
 *   </div>
 */

import { POPUP_DEFAULT_GRADIENT } from '@/components/popup/PopupHeader';

/**
 * The shared module masthead surface (`module-header`, index.css — deep state gold
 * in light mode, navy in dark), the same one the shared PopupHeader paints, so WMS
 * popups match the page headers and every other module's popups.
 */
export const WMS_POPUP_GRADIENT = POPUP_DEFAULT_GRADIENT;

/** Signal colours for action popups — same shape as the teal header. */
export const WMS_POPUP_TONE = {
  teal: WMS_POPUP_GRADIENT,
  red: 'bg-gradient-to-r from-red-600 to-rose-600',
  green: 'bg-gradient-to-r from-emerald-600 to-green-600',
  amber: 'bg-gradient-to-r from-amber-500 to-orange-600',
} as const;

export type WmsPopupTone = keyof typeof WMS_POPUP_TONE;

/** The bar's shape without its colour. */
export const WMS_POPUP_HEADER_SHAPE =
  'flex-shrink-0 flex items-center justify-between gap-3 px-4 py-1.5 text-white border-b border-white/10';

/** The standard (teal) header bar. */
export const WMS_POPUP_HEADER = `${WMS_POPUP_HEADER_SHAPE} ${WMS_POPUP_GRADIENT}`;

/** Header bar in a given tone — for action popups whose colour carries meaning. */
export const wmsPopupHeader = (tone: WmsPopupTone = 'teal') => `${WMS_POPUP_HEADER_SHAPE} ${WMS_POPUP_TONE[tone]}`;

export const WMS_POPUP_TITLE = 'text-sm font-semibold text-white leading-tight truncate';
export const WMS_POPUP_SUBTITLE = 'text-[10px] leading-tight text-white/80 truncate';

/** 28px icon box beside the title. */
export const WMS_POPUP_ICON =
  'w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0 text-white text-base';

/** 28px icon-only header button — close, fullscreen, etc. */
export const WMS_POPUP_CLOSE =
  'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-white/80 hover:text-white hover:bg-white/20 transition-colors';

/** 28px-tall labelled header button (e.g. "Download PDF"). */
export const WMS_POPUP_BTN =
  'h-7 px-2.5 rounded-lg inline-flex items-center gap-1.5 flex-shrink-0 bg-white/15 hover:bg-white/25 text-white text-[11px] font-semibold transition-colors';
